import { useEffect } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Printer, Flame, Trophy, ClipboardCheck, BookOpen, MessageCircle, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { DashboardSkeleton, LoadError } from "@/components/PageState";

export default function Digest() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    api.get("/digest").then(({ data }) => setData(data)).catch((requestError)=>setError(requestError?.response?.data?.detail || "Weekly digest could not load."));
    api.get("/reflections/insights?days=7").then(({ data }) => setInsights(data)).catch(() => {});
  }, [retry]);
  if (error) return <LoadError message={error} onRetry={()=>{setError("");setRetry((value)=>value+1);}} testId="digest-load-error" />;
  if (!data) return <DashboardSkeleton testId="digest-loading" cards={5} />;

  const insightFor = (sid) => (insights?.students || []).find((x) => x.student.id === sid);

  return (
    <div className="max-w-3xl mx-auto" data-testid="digest-page">
      <div className="print:hidden flex items-center justify-between mb-6">
        <div>
          <div className="chip mb-2">Weekly digest</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">This week at Oceanic Learn</h1>
          <p className="text-slate-500 text-sm">Generated {new Date(data.generated_at).toLocaleDateString()}</p>
        </div>
        <button data-testid="digest-print" onClick={() => window.print()} className="px-5 py-2.5 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold flex items-center gap-2">
          <Printer size={16} /> Print / save
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-3xl p-8 print:border-0 print:shadow-none space-y-6">
        {data.students.length === 0 && <div className="text-slate-500 text-sm">Link a student first to see their weekly summary.</div>}
        {data.students.map((s) => (
          <section key={s.student.id} data-testid={`digest-student-${s.student.id}`}
            className="border-b border-slate-100 last:border-0 pb-6 last:pb-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display text-xl font-bold text-slate-900">{s.student.name}</div>
                <div className="text-xs text-slate-500">{s.student.points} points total{s.student.beach_location ? ` · ${s.student.beach_location}` : ""}</div>
              </div>
              <div className="text-amber-600 font-display text-2xl font-bold flex items-center gap-1"><Flame size={20} /> {s.streak}</div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
              <Kpi icon={BookOpen} label="Curriculum lessons completed" value={s.lessons_done} testId={`digest-lessons-${s.student.id}`} />
              <Kpi icon={ClipboardCheck} label="Assignments turned in" value={s.assignments_turned_in} testId={`digest-turned-in-${s.student.id}`} />
              <Kpi icon={ClipboardCheck} label="Assignments graded" value={s.assignments_graded} testId={`digest-graded-${s.student.id}`} />
              <Kpi icon={ClipboardCheck} label="Worksheets graded" value={s.worksheets_done} />
              <Kpi icon={Trophy} label="New badges" value={s.new_badges} />
            </div>
            {s.try_next_subject && (
              <div className="mt-4 p-4 rounded-2xl bg-sky-50 border border-sky-100 text-sm">
                <span className="font-semibold text-slate-900">Try next week:</span> Pick a lesson in <b>{s.try_next_subject}</b> to keep things balanced.
              </div>
            )}
            {/* Reflection Insights */}
            {(() => {
              const ins = insightFor(s.student.id);
              if (!ins || ins.count === 0) return null;
              return (
                <div className="mt-4 p-4 rounded-2xl bg-amber-50 border border-amber-100" data-testid={`digest-insights-${s.student.id}`}>
                  <div className="flex items-center gap-2 text-amber-800 mb-2"><Sparkles size={14} /><b className="text-sm">Standalone lesson reflections ({ins.count})</b></div>
                  {ins.top_focus_keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {ins.top_focus_keywords.map((k) => (
                        <span key={k.word} className="chip capitalize" style={{background:"hsl(38 92% 92%)",color:"hsl(38 92% 25%)",borderColor:"hsl(38 60% 82%)"}}>{k.word} · {k.count}</span>
                      ))}
                    </div>
                  )}
                  <ul className="text-sm text-slate-700 space-y-1">
                    {ins.recent.slice(0, 3).map((r) => (
                      <li key={r.id}><b>Learned:</b> {r.learned}{r.next_focus ? <> · <span className="text-slate-500">wants next:</span> {r.next_focus}</> : null}</li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            <div className="mt-3 print:hidden">
              <Link to={`/portfolio?student_id=${s.student.id}`} data-testid={`digest-portfolio-${s.student.id}`}
                className="text-sm text-teal-700 font-semibold">View portfolio →</Link>
            </div>
          </section>
        ))}
      </div>

      <style>{`@media print { nav, aside, header, .print\\:hidden { display: none !important; } main { padding: 0 !important; } }`}</style>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testId }) {
  return (
    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
      <div className="text-xs text-slate-500 uppercase tracking-widest flex items-center gap-1"><Icon size={12} /> {label}</div>
      <div data-testid={testId} className="font-display text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}
